import { useState, useId, useRef } from 'react'
import { useDropdown } from '../../../shared/utils/DropdownContext'
import { useQueryClient } from '@tanstack/react-query'
import { metaDataApi } from '../api/metaDataApi'

type Option = { title: string; value: string }

interface CategorySelectProps {
  label?: string
  options: Option[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
}

export const CategorySelect = ({ label, options, value, onChange, placeholder = 'Select category', className = '' }: CategorySelectProps) => {
  const dropdownId = useId()
  const { openDropdown, setOpenDropdown } = useDropdown()
  const showDrop = openDropdown === dropdownId
  const queryClient = useQueryClient()

  const [isAdding, setIsAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find(o => o.value === value)
  const displayText = selectedOption?.title || placeholder

  const handleSelect = (option: Option) => {
    onChange?.(option.value)
    setOpenDropdown(null)
    setIsAdding(false)
    setNewName('')
  }

  const handleNewCategoryClick = () => {
    setIsAdding(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleSubmit = async () => {
    if (!newName.trim() || isLoading) return
    setIsLoading(true)
    try {
      await metaDataApi.createCategory(newName.trim())
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setNewName('')
      setIsAdding(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      {label && <label className="block text-[14px] font-[600] text-foreground mb-1">{label}</label>}
      <div className={`relative ${className}`}>
        <div
          className="h-[38px] rounded-[10px] flex items-center justify-between p-2 cursor-pointer bg-muted hover:bg-muted border-[1px] border-[#D9D9D9]"
          onClick={() => setOpenDropdown(showDrop ? null : dropdownId)}
        >
          <span className="text-foreground text-[14px]">{displayText}</span>
          <svg width="12" height="7" viewBox="0 0 12 7" fill="none" className={`transform transition-transform ${showDrop ? 'rotate-180' : ''}`}>
            <path d="M10.1693 1.3335L6.0026 5.50016L1.83594 1.3335" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {showDrop && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card rounded-[10px] p-3 z-10" style={{ boxShadow: '0px 13px 15px 5px #0302084D' }}>
            {options.map(option => (
              <div
                key={option.value}
                className="flex justify-between items-center p-2 hover:bg-muted rounded cursor-pointer"
                onClick={() => handleSelect(option)}
              >
                <span className="text-foreground font-[400] text-[16px]">{option.title}</span>
                {value === option.value && (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3307 4L5.9974 11.3333L2.66406 8" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            ))}

            <div className="mt-1">
              {isAdding ? (
                <div className="flex items-center gap-2 px-2 py-1 border border-[#D9D9D9] rounded-[8px]">
                  <input
                    ref={inputRef}
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    placeholder="New Category"
                    className="flex-1 text-[14px] outline-none bg-transparent text-foreground placeholder-[#B8B5B6]"
                  />
                  <button onClick={handleSubmit} disabled={isLoading} className="flex-shrink-0">
                    {isLoading ? (
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="#B8B5B6" strokeWidth="2" />
                        <path d="M8 2a6 6 0 0 1 6 6" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.3307 4L5.9974 11.3333L2.66406 8" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleNewCategoryClick}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#3F3E42] text-white rounded-[8px] text-[14px] font-[500] hover:opacity-90"
                >
                  <span>New Category</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3.33334V12.6667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3.33334 8H12.6667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
